import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/* ─────────────────────────────────────────────
   YOUR SCHOLARSHIP FINDER — SOCIAL LANDING PAGE
   Ads landing page — matches homepage v2 design
   Checkout routes through /{planType}-social for Meta CAPI
   S63 — Ported from LandingPageV2
   ───────────────────────────────────────────── */

// ── Design tokens ──
const T = {
  navy:      '#10314F',
  navyLight: '#33475C',
  navyMuted: '#5A6B7D',
  navyFaint: '#B9C6D4',
  navyBorder:'#2A4B6B',
  gold:      '#EF9F27',
  goldDark:  '#9A5B06',
  goldLight: '#FCEBD0',
  bg:        '#F7F5F0',
  border:    '#E6E1D6',
  white:     '#FFFFFF',
  lightText: '#E3EAF2',
  serif:     "Fraunces, Georgia, serif",
  sans:      "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

// ── Responsive style injection ──
const responsiveCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Inter:wght@400;500;600;700&display=swap');

  .lpv2-hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
  .lpv2-who-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
  .lpv2-chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; justify-items: center; margin-top: 28px; }
  .lpv2-aid-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
  .lpv2-steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
  .lpv2-dash-grid { display: grid; grid-template-columns: 5fr 7fr; gap: 28px; align-items: stretch; }
  .lpv2-problem-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
  .lpv2-features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
  .lpv2-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; max-width: 960px; margin: 0 auto; }
  .lpv2-faq-grid { display: grid; grid-template-columns: 340px 1fr; gap: 72px; align-items: start; }
  .lpv2-why-grid { display: grid; grid-template-columns: 7fr 5fr; gap: 64px; align-items: center; }
  .lpv2-giveback-flex { display: flex; align-items: center; justify-content: space-between; gap: 40px; }
  .lpv2-nav-links { display: flex; align-items: center; gap: 36px; }
  .lpv2-nav-mobile-btn { display: none; }

  @media (max-width: 1024px) {
    .lpv2-hero-grid { grid-template-columns: 1fr; gap: 40px; }
    .lpv2-steps-grid { grid-template-columns: 1fr 1fr; }
    .lpv2-features-grid { grid-template-columns: 1fr 1fr; }
    .lpv2-faq-grid { grid-template-columns: 1fr; gap: 40px; }
    .lpv2-dash-grid { grid-template-columns: 1fr; }
    .lpv2-problem-grid { grid-template-columns: 1fr; gap: 40px; }
    .lpv2-why-grid { grid-template-columns: 1fr; gap: 32px; }
    .lpv2-pricing-grid { grid-template-columns: 1fr; max-width: 480px; }
  }

  @media (max-width: 768px) {
    .lpv2-who-grid { grid-template-columns: 1fr; }
    .lpv2-aid-grid { grid-template-columns: 1fr; }
    .lpv2-chart-grid { grid-template-columns: 1fr; }
    .lpv2-steps-grid { grid-template-columns: 1fr; }
    .lpv2-features-grid { grid-template-columns: 1fr; }
    .lpv2-giveback-flex { flex-direction: column; text-align: center; }
    .lpv2-nav-links { display: none; }
    .lpv2-nav-mobile-btn { display: block; }

    /* ── Mobile padding overrides ── */
    .lpv2-section-pad { padding-left: 20px !important; padding-right: 20px !important; }
    .lpv2-nav-pad { padding-left: 20px !important; padding-right: 20px !important; }
    .lpv2-section-inner { padding-left: 20px !important; padding-right: 20px !important; }

    /* Hero mobile */
    .lpv2-hero-grid { gap: 32px !important; }
    .lpv2-hero-grid h1 { font-size: 36px !important; }
    .lpv2-hero-grid .lpv2-hero-subtitle { font-size: 15px !important; }

    /* Match cards: tighter on mobile */
    .lpv2-match-card { padding: 12px !important; gap: 10px !important; }
    .lpv2-match-score { width: 42px !important; height: 42px !important; font-size: 18px !important; }
    .lpv2-match-reasons { font-size: 12px !important; }
    .lpv2-match-due { font-size: 11px !important; padding: 4px 7px !important; }

    /* Section headings mobile */
    .lpv2-section-pad h2 { font-size: 30px !important; }

    /* Pricing cards */
    .lpv2-pricing-grid { gap: 20px !important; }

    /* FAQ grid */
    .lpv2-faq-grid { gap: 24px !important; }

    /* Dashboard grid cards */
    .lpv2-dash-grid { gap: 20px !important; }
    .lpv2-dash-stats { grid-template-columns: repeat(2, 1fr) !important; }

    /* Problem grid heading */
    .lpv2-problem-grid h2 { font-size: 32px !important; }

    /* Why we built this heading */
    .lpv2-why-heading { font-size: 30px !important; }

    /* Final CTA heading */
    .lpv2-final-cta h2 { font-size: 36px !important; }

    /* Video button: stack vertically on mobile */
    .lpv2-video-btn { flex-direction: column !important; padding: 12px !important; }
    .lpv2-video-thumb { width: 100% !important; height: auto !important; aspect-ratio: 16/9; }

    /* Stat callout */
    .lpv2-stat-callout p { font-size: 20px !important; }
  }

  /* FAQ accordion */
  .lpv2-faq-answer { max-height: 0; overflow: hidden; transition: max-height 0.3s ease, padding 0.3s ease; }
  .lpv2-faq-answer.open { max-height: 2000px; padding-bottom: 8px; }
  .lpv2-faq-plus { transition: transform 0.2s ease; display: inline-block; }
  .lpv2-faq-plus.open { transform: rotate(45deg); }

  /* Video modal */
  .lpv2-video-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(0,0,0,0.85);
    display: flex; align-items: center; justify-content: center;
    animation: lpv2-fadein 0.2s ease;
  }
  @keyframes lpv2-fadein { from { opacity: 0; } to { opacity: 1; } }

  /* Chart image zoom on click */
  .lpv2-chart-zoom { cursor: pointer; transition: transform 0.2s ease; }
  .lpv2-chart-zoom:hover { transform: scale(1.02); }
  .lpv2-chart-lightbox {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(0,0,0,0.85);
    display: flex; align-items: center; justify-content: center;
    animation: lpv2-fadein 0.2s ease;
    cursor: pointer;
  }
`;

// ── Section wrapper ──
const Section = ({ bg, style, children, id }) => (
  <section id={id} style={{ padding: '96px 0', background: bg || 'transparent', ...style }}>
    <div className="lpv2-section-inner" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 80px', boxSizing: 'border-box' }}>
      {children}
    </div>
  </section>
);

// ── Eyebrow label ──
const Eyebrow = ({ children }) => (
  <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.goldDark }}>
    {children}
  </div>
);

// ── Section heading (scaled down from 52 → 38) ──
const SectionHeading = ({ children, style }) => (
  <h2 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 38, lineHeight: 1.12, letterSpacing: '-0.01em', color: T.navy, ...style }}>
    {children}
  </h2>
);

// ── FAQ data (updated per Matt's Sep 28 email) ──
const FAQ_DATA = [
  {
    q: 'Why should I pay when other sites are free?',
    a: "Our own kids spent hours on free scholarship sites and never got to the part that matters: applying. When we looked for help, it felt like families had two choices. Keep using free sites that leave all the sorting to you, which is slow, painful, and wears students down before they ever apply, or hire a private consultant at $3,000 to $10,000. We didn\u2019t think anyone should have to pick between lost weekends and a bill in the thousands.\n\nSo we built the middle option: matches based on your student\u2019s profile and the reason each one fits. Less time searching, more time applying. That\u2019s how your student gets in the game.\n\nStop scrolling. Start applying."
  },
  {
    q: 'When is the best time to sign up?',
    rich: 'deadline'
  },
  {
    q: 'Where do your scholarships come from?',
    a: "Two places. The first is an established education data provider with decades in the field, the same starting point most reputable scholarship databases use. The second is public federal filings from private foundations: awards that are documented and funded but rarely show up in consumer search tools. Two sources means a wider pool to match your student against, and more chances to find scholarships that actually fit.\n\nWe also check application links and remove the dead ones, so your student\u2019s time goes into applying, not chasing broken pages. If something ever looks off, tell us and we\u2019ll take a look.\n\nOne thing you\u2019ll never see from us: an application fee, a request for a Social Security number, or a \u201Cguaranteed\u201D award. Those are signs of a scam, and legitimate scholarships don\u2019t work that way."
  },
  {
    q: 'What information does my student need to provide?',
    a: "Completing a profile takes about 3 minutes. Core info includes academics (GPA, test scores), activities, location, and intended major. Optional fields like race, religion, background, or household income unlock targeted, high-value niche scholarships. You can skip any optional field, though a more complete profile yields better match accuracy."
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. If you\u2019re not satisfied within the first 7 days, we\u2019ll give you a full refund \u2014 no questions asked. After that, you can cancel directly in your Account Settings in two clicks \u2014 no phone calls, chat agents, or cancellation hoops. You retain full access through the end of your billing cycle and won\u2019t be charged again."
  },
  {
    q: 'Is scholarship searching over once my student starts college?',
    rich: 'college'
  },
  {
    q: "Isn\u2019t this the same as the scholarships colleges offer?",
    a: "No, and this confuses almost every parent the first time through. We were confused too. When a college talks about \u201Cscholarships,\u201D it usually means its own money: merit aid based on GPA, test scores, and sometimes activities. That money comes from the college, and your student gets it by applying and being admitted. You don\u2019t need us for that. Your Scholarship Finder finds everything else: outside scholarships funded by companies, foundations, community organizations, and associations. These have their own applications, deadlines, and eligibility rules, and they\u2019re scattered across thousands of sources. That\u2019s the part that eats up hours of scrolling, and that\u2019s the part we fix. Think of it in two layers. First comes the college\u2019s merit aid, which is decided by the school. Second come the outside scholarships your student wins, which they earn by applying. Outside awards follow your student to whatever college they choose."
  },
  {
    q: "What\u2019s the difference between Individual and Family?",
    a: "Individual covers one student profile with dedicated matches and application tracking. Family covers up to three students under a single account. Each child gets their own unique profile and match results, while parents manage everything from one unified dashboard \u2014 at a more affordable per-student price."
  },
  {
    q: 'Can I switch between plans?',
    a: "You can upgrade from Individual to Family at any time \u2014 we\u2019ll prorate the difference for the time remaining on your subscription. To downgrade from Family to Individual, contact us at theteam@yourscholarshipfinder.com and we\u2019ll help you make the switch."
  }
];


// ═══════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════
export default function SocialLanding() {
  const [showVideo, setShowVideo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [zoomedChart, setZoomedChart] = useState(null);

  // Handle incoming hash links (e.g. from Our Story → /#pricing)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  // Close chart lightbox on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (zoomedChart) setZoomedChart(null);
        if (showVideo) setShowVideo(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [zoomedChart, showVideo]);

  // Reset checkout loading state when returning via browser back button
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) setCheckoutLoading(null);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Inject responsive CSS
  useEffect(() => {
    const id = 'slp-responsive-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = responsiveCSS;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleCheckout = (plan) => {
    setCheckoutLoading(plan);
    window.location.href = `/${plan}-social`;
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // ── Logo SVG ──
  const LogoSVG = () => (
    <svg width="44" height="44" viewBox="0 0 100 100" aria-hidden="true">
      <rect x="0" y="0" width="100" height="100" rx="24" fill={T.navy} />
      <path d="M50 18 C36 18 26 29 26 42 C26 58 50 84 50 84 C50 84 74 58 74 42 C74 29 64 18 50 18 Z" fill={T.white} />
      <circle cx="50" cy="41" r="8.5" fill={T.gold} />
    </svg>
  );

  // ── Check icon ──
  const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );

  // ── Render FAQ answer (plain text or rich content with charts) ──
  const renderFaqAnswer = (faq) => {
    const textStyle = { fontSize: 15.5, lineHeight: 1.75, color: T.navyLight, maxWidth: 720 };
    if (faq.rich === 'deadline') {
      return (
        <div style={textStyle}>
          <p style={{ margin: '0 0 14px' }}>Now. Scholarship deadlines don't follow the school calendar. They land in every month of the year.</p>
          <img src="/images/deadlines_by_month_cropped.png" alt="Share of scholarship deadlines by month — peaks at 24.5% in March" style={{ width: '100%', maxWidth: 420, borderRadius: 10, margin: '8px 0 14px' }} />
          <p style={{ margin: '0 0 14px' }}>Deadlines build through the fall and winter and peak in March, when about 1 in 4 are due. Even the quietest month still has deadlines, and nearly 1 in 3 fall between September and January.</p>
          <p style={{ margin: '0 0 14px' }}>That means there's no "scholarship season" to wait for. Every month you wait, that month's deadlines close without your student in the running. Starting early also gives your student time to write strong applications ahead of the spring peak, instead of rushing several at once.</p>
          <p style={{ margin: 0 }}>Your membership runs for a full year, so it covers every month on this chart.</p>
        </div>
      );
    }
    if (faq.rich === 'college') {
      return (
        <div style={textStyle}>
          <p style={{ margin: '0 0 14px' }}>No. Senior year of high school is when the most scholarships open up, but thousands more are available at every stage of college. Many are open only to students already enrolled, including awards tied to a specific major, year in school, or career path. Your student can keep finding and applying every year until graduation (and beyond).</p>
          <img src="/images/by_year_rounded_cropped.png" alt="Scholarships available at each stage — 7,100+ freshman through 6,000+ senior year" style={{ width: '100%', maxWidth: 420, borderRadius: 10, margin: '8px 0 14px' }} />
          <p style={{ margin: 0 }}>That's why one-time awards add up. A $1,000 scholarship won each year is $4,000 across four years.</p>
        </div>
      );
    }
    const paragraphs = (faq.a || '').split('\n\n');
    return (
      <div style={textStyle}>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ margin: i < paragraphs.length - 1 ? '0 0 14px' : 0 }}>{p}</p>
        ))}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: T.sans, color: T.navy, background: T.bg, minHeight: '100vh' }}>

      {/* ══════ NAV ══════ */}
      <header className="lpv2-nav-pad" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 80px', background: T.white, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <Link to="/social" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <LogoSVG />
          <span style={{ fontFamily: T.serif, fontSize: 22, lineHeight: 1, color: T.navy }}>
            <span style={{ fontStyle: 'italic', fontWeight: 400, color: T.navyMuted }}>Your</span>{' '}
            <span style={{ fontWeight: 500 }}>Scholarship Finder</span>
          </span>
        </Link>
        <nav className="lpv2-nav-links" style={{ fontSize: 15, fontWeight: 500 }}>
          <a href="#who" onClick={(e) => { e.preventDefault(); scrollTo('who'); }} style={{ textDecoration: 'none', color: T.navy }}>Who it's for</a>
          <a href="#how" onClick={(e) => { e.preventDefault(); scrollTo('how'); }} style={{ textDecoration: 'none', color: T.navy }}>How it works</a>
          <Link to="/social/our-story" style={{ textDecoration: 'none', color: T.navy }}>Our story</Link>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ textDecoration: 'none', color: T.navy }}>Pricing</a>
          <Link to="/login" style={{ textDecoration: 'none', color: T.navy }}>Log in</Link>
          <button onClick={() => scrollTo('pricing')} style={{ background: T.navy, color: T.white, padding: '11px 20px', borderRadius: 10, fontWeight: 600, fontSize: 15, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
            Get started
          </button>
        </nav>
        <button className="lpv2-nav-mobile-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 72, zIndex: 99 }}>
          <a href="#who" onClick={(e) => { e.preventDefault(); scrollTo('who'); }} style={{ textDecoration: 'none', color: T.navy, fontWeight: 500, fontSize: 15 }}>Who it's for</a>
          <a href="#how" onClick={(e) => { e.preventDefault(); scrollTo('how'); }} style={{ textDecoration: 'none', color: T.navy, fontWeight: 500, fontSize: 15 }}>How it works</a>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ textDecoration: 'none', color: T.navy, fontWeight: 500, fontSize: 15 }}>Pricing</a>
          <Link to="/social/our-story" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: 'none', color: T.navy, fontWeight: 500, fontSize: 15 }}>Our story</Link>
          <Link to="/login" style={{ textDecoration: 'none', color: T.navy, fontWeight: 500, fontSize: 15 }}>Log in</Link>
          <button onClick={() => scrollTo('pricing')} style={{ background: T.navy, color: T.white, padding: '11px 20px', borderRadius: 10, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: T.sans, fontSize: 15 }}>Get started</button>
        </div>
      )}


      {/* ══════ HERO ══════ */}
      <section className="lpv2-section-pad" style={{ background: T.white, padding: '80px 80px 88px' }}>
        <div className="lpv2-hero-grid" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 10, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 999, padding: '7px 14px', fontSize: 13.5, fontWeight: 600, color: T.navy }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.gold }} />
              For parents of high school seniors and college students
            </div>
            <h1 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 52, lineHeight: 1.08, letterSpacing: '-0.015em', color: T.navy }}>
              We find the scholarships.{' '}
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>Your student applies.</span>
            </h1>
            {/* HERO SUBTITLE — Matt's pick (Option B, approved by Shannon) */}
            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: T.navyLight, maxWidth: 540 }}>
              Your student shouldn't spend more time searching for scholarships than applying. We match them to the ones they can actually win. No searching. Just applying.
            </p>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => scrollTo('pricing')} style={{ background: T.gold, color: T.navy, padding: '15px 24px', borderRadius: 10, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
                Get your student's matches →
              </button>
            </div>
            <div style={{ display: 'flex', gap: 20, fontSize: 14, color: T.navyLight, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><CheckIcon /> 100% outside scholarships</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><CheckIcon /> Transparent match logic</span>
            </div>
          </div>

          {/* Sample matches card */}
          <div style={{ background: T.bg, borderRadius: 20, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500 }}>Your student's matches</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.navyMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sample</div>
            </div>
            {[
              { score: 96, name: 'Regional Youth Hockey Scholarship', reasons: 'Colorado resident ✓ · Plays club hockey ✓ · Senior ✓', due: 'Due Nov 15' },
              { score: 91, name: 'Community Foundation Service Award', reasons: 'Volunteer hours ✓ · GPA 3.5+ ✓ · Denver metro ✓', due: 'Due Dec 1' },
              { score: 88, name: 'Engineering Society Student Award', reasons: 'Intended STEM major ✓ · Current college student ✓', due: 'Due Jan 31' },
            ].map((m, i) => (
              <div key={i} className="lpv2-match-card" style={{ display: 'flex', gap: 14, alignItems: 'center', background: T.white, borderRadius: 14, padding: 16 }}>
                <div className="lpv2-match-score" style={{ width: 50, height: 50, flexShrink: 0, borderRadius: 12, background: T.navy, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.serif, fontSize: 22, fontWeight: 600 }}>{m.score}</div>
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 600 }}>{m.name}</div>
                  <div className="lpv2-match-reasons" style={{ fontSize: 13, color: T.navyLight }}>{m.reasons}</div>
                </div>
                <div className="lpv2-match-due" style={{ fontSize: 13, fontWeight: 600, color: T.navy, background: T.goldLight, padding: '5px 9px', borderRadius: 7, whiteSpace: 'nowrap' }}>{m.due}</div>
              </div>
            ))}
            <div style={{ fontSize: 13, color: T.navyMuted, padding: '2px 4px 0' }}>
              Every match comes with the reason it fits, the exact deadline, and a direct application link.
            </div>
          </div>
        </div>
      </section>


      {/* ══════ WHY WE BUILT THIS — navy background per Matt ══════ */}
      <section className="lpv2-section-pad" style={{ padding: '80px 80px', background: T.navy }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.gold }}>
            Why we built this
          </div>
          <h2 className="lpv2-why-heading" style={{ margin: '14px 0 24px', fontFamily: T.serif, fontWeight: 500, fontSize: 38, lineHeight: 1.12, letterSpacing: '-0.01em', color: T.white }}>
            We thought our kids were being lazy.{' '}
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>We were wrong.</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 17, lineHeight: 1.7, color: T.lightText }}>
            <p style={{ margin: 0 }}>My wife and I told our kids we expected them to apply for scholarships. They spent hours on the free sites, applied to a couple, and gave up. We thought they weren't trying, so we logged in to see for ourselves.</p>
            <p style={{ margin: 0 }}>Cue the endless scrolling. Page after page of things they could never win.</p>
            <p style={{ margin: 0, fontFamily: T.serif, fontSize: 22, lineHeight: 1.35, color: T.white, borderLeft: `3px solid ${T.gold}`, paddingLeft: 20 }}>They weren't quitting. The tools were broken. So we built one that works: outside scholarships your student can actually win, so the hours go into applications, not scrolling.</p>
          </div>

        </div>
      </section>


      {/* ══════ WHO IT'S FOR ══════ */}
      <Section id="who">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 780, marginBottom: 44 }}>
          <Eyebrow>Who it's for</Eyebrow>
        </div>
        <div className="lpv2-who-grid">
          {[
            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10L12 5 2 10l10 5 10-5z"/><path d="M6 12v5c3 2 9 2 12 0v-5"/></svg>, title: 'Parents of high school seniors', text: "Start early so your student capitalizes on the full application season \u2014 not just the few awards remaining after graduation.", cta: "Start your senior's matches \u2192" },
            { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 2v4M16 2v4"/></svg>, title: 'Parents of college students', text: "Scholarship opportunities don't end at high school graduation. Thousands of awards are reserved exclusively for enrolled college students, many requiring renewal applications each year. Your student can keep applying all four years.", cta: "Start your college student's matches \u2192" },
          ].map((card, i) => (
            <div key={i} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 36, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{card.icon}</div>
              <h3 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 26 }}>{card.title}</h3>
              <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: T.navyLight }}>{card.text}</p>
              <button onClick={() => scrollTo('pricing')} style={{ marginTop: 4, fontSize: 15, fontWeight: 700, color: T.navy, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.sans, textAlign: 'left', padding: 0 }}>{card.cta}</button>
            </div>
          ))}
        </div>
        <div className="lpv2-chart-grid">
          <img src="/images/deadlines_by_month_cropped.png" alt="Chart: share of scholarship deadlines by month, peaking in March at 24.5 percent." className="lpv2-chart-zoom" onClick={() => setZoomedChart('/images/deadlines_by_month_cropped.png')} style={{ width: '100%', maxWidth: 460, height: 'auto', borderRadius: 16, border: `1px solid ${T.border}` }} />
          <img src="/images/by_year_rounded_cropped.png" alt="Chart: scholarships available at each stage — 7,100+ freshman through 6,000+ senior." className="lpv2-chart-zoom" onClick={() => setZoomedChart('/images/by_year_rounded_cropped.png')} style={{ width: '100%', maxWidth: 460, height: 'auto', borderRadius: 16 }} />
        </div>
        <div style={{ textAlign: 'center', marginTop: 44 }}>
          <button onClick={() => scrollTo('pricing')} style={{ background: T.gold, color: T.navy, padding: '15px 24px', borderRadius: 10, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
            Get your student's matches →
          </button>
        </div>
      </Section>


      {/* ══════ OUTSIDE VS COLLEGE AID ══════ */}
      <Section bg={T.white}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 780, marginBottom: 40 }}>
          <Eyebrow>Outside scholarships</Eyebrow>
          <SectionHeading>This isn't the aid your college offers.</SectionHeading>
          {/* UPDATED: "Most" → "Many" per Annette/Matt */}
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: T.navyLight }}>Many parents mix these up. We did too. Here's the difference, and the part we help with.</p>
        </div>
        <div className="lpv2-aid-grid">
          <div style={{ background: T.bg, borderRadius: 20, padding: 36, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <h3 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 24, color: T.navyLight }}>College merit scholarships</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 16, lineHeight: 1.5, color: T.navyLight }}>
              <div>• Merit and need-based awards from the college itself</div>
              <div>• Offered when your student is admitted</div>
              <div>• Only counts at that school</div>
            </div>
          </div>
          <div style={{ background: T.navy, color: T.white, borderRadius: 20, padding: 36, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 24, color: T.white }}>Outside scholarships</h3>
              <span style={{ background: T.gold, color: T.navy, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999 }}>What we match</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 16, lineHeight: 1.5, color: T.lightText }}>
              <div>• Funded by foundations, businesses, community groups, and employers</div>
              <div>• Your student finds and applies for them separately</div>
              <div>• Open to high school seniors and current college students</div>
            </div>
          </div>
        </div>
        {/* REMOVED: haystack/needle tagline per Matt/Annette agreement */}
      </Section>


      {/* ══════ HOW IT WORKS ══════ */}
      <Section id="how">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 780, marginBottom: 44 }}>
          <Eyebrow>How it works</Eyebrow>
          <SectionHeading>No searching. Just applying.</SectionHeading>
        </div>
        {/* UPDATED: "Take a glimpse inside" per Annette/Matt */}
        <button className="lpv2-video-btn" onClick={() => setShowVideo(true)} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 18, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '12px 24px 12px 12px', cursor: 'pointer', marginBottom: 44, fontFamily: T.sans }}>
                   <div className="lpv2-video-thumb" style={{ width: 160, height: 90, flexShrink: 0, borderRadius: 10, position: 'relative', overflow: 'hidden' }}>
            <img src="/images/dashboard_preview.png" alt="Student dashboard preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,49,79,0.35)' }}>
              <svg width="36" height="36" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill={T.gold} /><path d="M10 8l6 4-6 4z" fill={T.navy} /></svg>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
            <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500, color: T.navy }}>Take a glimpse inside</div>
            <div style={{ fontSize: 14, color: T.navyLight }}>A quick look at the student and parent dashboards.</div>
          </div>
        </button>
        {/* UPDATED: Steps reverted to live wording per Annette, minus "financials" per Matt */}
        <div className="lpv2-steps-grid">
          {[
            { num: '01', title: 'Build your profile', text: 'Input your academics, activities, interests, and background. Takes about 3 minutes.' },
            { num: '02', title: 'Matches in 60 seconds', text: "Our engine scores every scholarship against your real profile \u2014 GPA, activities, background, and school choice." },
            { num: '03', title: 'See why you matched', text: 'Clear criteria breakdowns, match scores, and step-by-step application advice for every result. No black boxes.' },
            { num: '04', title: 'Track & apply', text: 'Mark scholarships as Saved, In Progress, Submitted, or Awarded. Filter by deadline, score, or category.' },
          ].map((step, i) => (
            <div key={i} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontFamily: T.serif, fontSize: 36, fontWeight: 500, color: T.goldDark, lineHeight: 1 }}>{step.num}</div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{step.title}</h3>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: T.navyLight }}>{step.text}</p>
            </div>
          ))}
        </div>
      </Section>


      {/* ══════ TWO DASHBOARDS ══════ */}
      <Section bg={T.white} id="dashboards">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 820, marginBottom: 44 }}>
          <Eyebrow>Two dashboards</Eyebrow>
          <SectionHeading>
            Your student applies.{' '}
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>You see the progress.</span>
          </SectionHeading>
          {/* UPDATED: "no nagging required" per Annette/Matt */}
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: T.navyLight }}>
            Every membership includes separate student and parent dashboards. Your student works through their matches. You see what they've saved, started, submitted, and won — no nagging required.
          </p>
        </div>
        <div className="lpv2-dash-grid">
          {/* Student dashboard preview */}
          <div style={{ background: T.bg, borderRadius: 20, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500 }}>Student dashboard</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.navyMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sample</div>
            </div>
            <div style={{ fontSize: 14, color: T.navyLight, marginBottom: 2 }}>Their matches, reasons, deadlines, and applications in one place.</div>
            {[
              { score: 96, name: 'Regional Youth Hockey Scholarship', sub: 'Due Nov 15 · 3 of 3 criteria met', action: 'Continue' },
              { score: 91, name: 'Community Foundation Service Award', sub: 'Due Dec 1 · 3 of 3 criteria met', action: 'Start' },
              { score: 88, name: 'Local Credit Union Scholarship', sub: 'Due Jan 10 · 2 of 2 criteria met', action: 'Save' },
            ].map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 10, background: T.navy, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.serif, fontSize: 17, fontWeight: 600 }}>{m.score}</div>
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: T.navyLight }}>{m.sub}</div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.navy, border: `1.5px solid ${T.navy}`, padding: '5px 9px', borderRadius: 7, whiteSpace: 'nowrap' }}>{m.action}</span>
              </div>
            ))}
          </div>
          {/* Parent dashboard preview */}
          <div style={{ background: T.navy, borderRadius: 20, padding: 24, display: 'flex', flexDirection: 'column', gap: 14, color: T.white }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500, color: T.white }}>Parent dashboard</div>
                <span style={{ background: T.gold, color: T.navy, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 999 }}>For you</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.navyFaint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sample</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ background: T.white, color: T.navy, fontSize: 13, fontWeight: 700, padding: '8px 12px', borderRadius: 8 }}>Emma · Senior</span>
              <span style={{ border: `1px solid ${T.navyBorder}`, color: T.lightText, fontSize: 13, fontWeight: 600, padding: '8px 12px', borderRadius: 8 }}>Jack · College sophomore</span>
            </div>
            <div className="lpv2-dash-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {[{ n: 9, l: 'Saved' }, { n: 3, l: 'In progress' }, { n: 6, l: 'Submitted' }, { n: 1, l: 'Awarded' }].map((s, i) => (
                <div key={i} style={{ background: T.white, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ fontFamily: T.serif, fontSize: 30, fontWeight: 500, lineHeight: 1, color: T.navy }}>{s.n}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.navyLight }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: T.navyFaint, textTransform: 'uppercase', letterSpacing: '0.08em', paddingTop: 2 }}>Coming up</div>
            {[
              { name: 'Regional Youth Hockey Scholarship', status: 'In progress', statusBg: T.goldLight, date: 'Nov 15' },
              { name: 'Community Foundation Service Award', status: 'Not started', statusBg: T.border, date: 'Dec 1' },
              { name: 'Local Credit Union Scholarship', status: 'Saved', statusBg: T.border, date: 'Jan 10' },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: T.white, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>{r.name}</div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.navy, background: r.statusBg, padding: '4px 8px', borderRadius: 6 }}>{r.status}</span>
                  <span style={{ fontSize: 12, color: T.navyMuted, width: 56, textAlign: 'right' }}>{r.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 44 }}>
          <button onClick={() => scrollTo('pricing')} style={{ background: T.gold, color: T.navy, padding: '15px 24px', borderRadius: 10, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
            See pricing →
          </button>
        </div>
      </Section>


      {/* ══════ YOUR KID ISN'T THE PROBLEM ══════ */}
      <section className="lpv2-section-pad" style={{ padding: '88px 80px', background: T.navy, color: T.white }}>
        <div className="lpv2-problem-grid" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.gold }}>What changes at home</div>
            <h2 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 42, lineHeight: 1.08, letterSpacing: '-0.01em', color: T.white }}>Your kid isn't the problem.</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 17, lineHeight: 1.65, color: T.lightText }}>
            <p style={{ margin: 0 }}>When a student stops applying, it looks like they gave up. Almost always, they hit a wall: hundreds of results, a handful worth reading, nothing that fits.</p>
            <p style={{ margin: 0 }}>When the list is short, tailored, and winnable, the dynamic changes. You're not chasing them. They're applying on their own.</p>
          </div>
        </div>
      </section>


      {/* Founder strip removed — story now lives in "Why we built this" section above hero */}


      {/* ══════ WHAT'S INCLUDED ══════ */}
      <Section bg={T.bg}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 780, marginBottom: 44 }}>
          <Eyebrow>What's included</Eyebrow>
          <SectionHeading>You raised a great student. Now let's help them get funded.</SectionHeading>
        </div>
        {/* UPDATED: Annette's rewording for GPA, deadlines, links */}
        <div className="lpv2-features-grid">
          {[
            { title: 'Parent dashboard', text: "See what your student has saved, started, and submitted." },
            { title: 'Scores and reasons', text: "Every match ranked, with the exact criteria your student meets." },
            { title: 'Smart GPA matching', text: "Accurately evaluates both weighted and unweighted GPAs." },
            { title: 'Deadline sorting', text: "Easily keep track of upcoming deadlines." },
            { title: 'Direct application links', text: "Automated routines verify application links regularly." },
            { title: 'Application tracking', text: "Saved \u2192 in progress \u2192 submitted \u2192 awarded." },
            { title: 'Senior year through college', text: "The search doesn't stop after high school graduation." },
            { title: 'Private by design', text: "We never sell your student's data." },
          ].map((f, i) => (
            <div key={i} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 18, padding: '28px 26px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ width: 28, height: 3, borderRadius: 2, background: T.gold }} />
              <div style={{ fontSize: 17, fontWeight: 700, color: T.navy, paddingTop: 6 }}>{f.title}</div>
              <div style={{ fontSize: 14.5, lineHeight: 1.6, color: T.navyLight }}>{f.text}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 44 }}>
          <button onClick={() => scrollTo('pricing')} style={{ background: T.navy, color: T.white, padding: '15px 24px', borderRadius: 10, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
            Get your student's matches →
          </button>
        </div>
      </Section>


      {/* ══════ GIVE-BACK ══════ */}
      <section className="lpv2-section-pad" style={{ padding: '48px 80px', background: T.goldLight }}>
        <div className="lpv2-giveback-flex" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 26, lineHeight: 1.3, color: T.navy, maxWidth: 860 }}>
            10% of membership revenue goes to your student's designated high school.
          </p>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: T.navyLight, maxWidth: 340 }}>
            Schools receive an aggregate total. They never see which families joined.
          </p>
        </div>
      </section>


      {/* ══════ PRICING ══════ */}
      <Section id="pricing">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center', maxWidth: 780, margin: '0 auto 44px' }}>
          <Eyebrow>Pricing</Eyebrow>
          <SectionHeading style={{ textAlign: 'center' }}>One scholarship can cover it.</SectionHeading>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: T.navyLight }}>Both plans include every feature, student and parent dashboards included. The only difference is how many students.</p>
        </div>
        <div className="lpv2-stat-callout" style={{ textAlign: 'center', margin: '0 auto 36px', maxWidth: 700, padding: '20px 28px', background: T.white, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          <p style={{ margin: 0, fontFamily: T.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.3, color: T.navy }}>
            Students averaged <span style={{ color: T.goldDark, fontWeight: 600 }}>161 scholarship matches</span> worth over <span style={{ color: T.goldDark, fontWeight: 600 }}>$530,000</span> in potential awards.
          </p>
        </div>
        <div className="lpv2-pricing-grid">
          {/* Individual */}
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 36, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Individual</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontFamily: T.serif, fontSize: 48, fontWeight: 500, lineHeight: 1 }}>$47</span>
              <span style={{ fontSize: 16, color: T.navyMuted }}>/year</span>
            </div>
            <div style={{ fontSize: 15, color: T.navyLight }}>1 student · everything included</div>
            <button onClick={() => handleCheckout('individual')} disabled={!!checkoutLoading} style={{ textAlign: 'center', border: `1.5px solid ${T.navy}`, color: T.navy, padding: 14, borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer', background: 'transparent', fontFamily: T.sans, opacity: checkoutLoading ? 0.7 : 1 }}>
              {checkoutLoading === 'individual' ? 'Connecting...' : 'Get started'}
            </button>
            <div style={{ fontSize: 13, color: T.navyMuted, textAlign: 'center' }}>7-day money-back guarantee</div>
          </div>
          {/* Family */}
          <div style={{ background: T.navy, color: T.white, borderRadius: 20, padding: 36, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Family</div>
              <span style={{ background: T.gold, color: T.navy, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999 }}>Best value</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontFamily: T.serif, fontSize: 48, fontWeight: 500, lineHeight: 1, color: T.white }}>$97</span>
              <span style={{ fontSize: 16, color: T.navyFaint }}>/year</span>
            </div>
            <div style={{ fontSize: 15, color: T.lightText }}>Up to 3 students · separate matches for each, one parent dashboard for all</div>
            <button onClick={() => handleCheckout('family')} disabled={!!checkoutLoading} style={{ textAlign: 'center', background: T.gold, color: T.navy, padding: '15.5px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer', border: 'none', fontFamily: T.sans, opacity: checkoutLoading ? 0.7 : 1 }}>
              {checkoutLoading === 'family' ? 'Connecting...' : 'Get started'}
            </button>
            <div style={{ fontSize: 13, color: T.navyFaint, textAlign: 'center' }}>7-day money-back guarantee</div>
          </div>
        </div>
      </Section>


      {/* ══════ FAQ — React-controlled accordion (fixes +/× toggle bug) ══════ */}
      <section className="lpv2-section-pad" style={{ padding: '88px 80px', background: T.white }}>
        <div className="lpv2-faq-grid" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Eyebrow>FAQ</Eyebrow>
            <h2 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 36, lineHeight: 1.12 }}>Questions parents ask</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {FAQ_DATA.map((faq, i) => (
              <div key={i} style={{ borderTop: `1px solid ${T.border}`, padding: '22px 0', ...(i === FAQ_DATA.length - 1 ? { borderBottom: `1px solid ${T.border}` } : {}) }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, fontSize: 17, fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, color: T.navy, fontFamily: T.sans }}
                >
                  {faq.q}
                  <span className={`lpv2-faq-plus${openFaq === i ? ' open' : ''}`} style={{ color: T.goldDark, flexShrink: 0, fontSize: 20 }}>+</span>
                </button>
                <div className={`lpv2-faq-answer${openFaq === i ? ' open' : ''}`}>
                  {openFaq === i && renderFaqAnswer(faq)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══════ FINAL CTA + FOOTER ══════ */}
      <section className="lpv2-section-pad lpv2-final-cta" style={{ background: T.navy, color: T.white, padding: '96px 80px 44px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontFamily: T.serif, fontWeight: 500, fontSize: 52, lineHeight: 1.08, letterSpacing: '-0.015em', color: T.gold }}>
          Stop scrolling. Start applying.
        </h2>
        <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: T.lightText, maxWidth: 580 }}>
          Give your student the list we wish ours had. Profile in about 3 minutes, matches in 60 seconds.
        </p>
        <button onClick={() => scrollTo('pricing')} style={{ background: T.gold, color: T.navy, padding: '16px 28px', borderRadius: 10, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: T.sans }}>
          Get your student's matches →
        </button>
        <div style={{ flexGrow: 1, minHeight: 36 }} />
        <div style={{ width: '100%', borderTop: `1px solid ${T.navyBorder}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.navyFaint, flexWrap: 'wrap', gap: 14 }}>
          <span>© 2026 Your Scholarship Finder. A service of ScholarSync AI LLC.</span>
          <span>
            <Link to="/terms" style={{ color: T.navyFaint, textDecoration: 'none' }}>Terms of Service</Link>
            {' · '}
            <Link to="/privacy" style={{ color: T.navyFaint, textDecoration: 'none' }}>Privacy Policy</Link>
            {' · '}
            <a href="mailto:theteam@yourscholarshipfinder.com" style={{ color: T.navyFaint, textDecoration: 'none' }}>Contact</a>
          </span>
        </div>
      </section>


      {/* ══════ VIDEO MODAL ══════ */}
      {showVideo && (
        <div className="lpv2-video-overlay" onClick={() => setShowVideo(false)}>
          <div style={{ position: 'relative', width: '90%', maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowVideo(false)} style={{ position: 'absolute', top: -48, right: 0, background: 'none', border: 'none', color: T.white, fontSize: 32, cursor: 'pointer', fontFamily: T.sans }}>✕</button>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 14, overflow: 'hidden' }}>
              <iframe
                src="https://player.vimeo.com/video/1224903848?autoplay=1"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; fullscreen"
                allowFullScreen
                title="Take a glimpse inside Your Scholarship Finder"
              />
            </div>
          </div>
        </div>
      )}

      {/* ══════ CHART ZOOM LIGHTBOX ══════ */}
      {zoomedChart && (
        <div className="lpv2-chart-lightbox" onClick={() => setZoomedChart(null)}>
          <div style={{ position: 'relative', maxWidth: 900, width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoomedChart(null)} style={{ position: 'absolute', top: -48, right: 0, background: 'none', border: 'none', color: T.white, fontSize: 32, cursor: 'pointer', fontFamily: T.sans }}>✕</button>
            <img src={zoomedChart} alt="Chart — click anywhere to close" style={{ width: '100%', borderRadius: 14 }} />
          </div>
        </div>
      )}

    </div>
  );
}
